import React, { useState, useMemo, useEffect } from 'react'
import { connect } from 'react-redux'
import { buildRoutePath, routes } from '../../constants/routes'
import { withRouterProps } from '../utils/with-router-props'
import { saveSiteGuideVisitedRoute } from '../../reducers/site-guide'
import { completeAnimatingIn, completeAnimatingOut, completePreloading, saveNavTheme } from '../../reducers/nav'
import { calcComponentState } from '../../fxs/component-utils'
import { ReactComponent as MarkerStroke } from '../../assets/svgs/underline-01.svg'

import anime from 'animejs'
import { clearMusicSource } from '../../reducers/sound'
import { Link } from 'react-router-dom'

const data = [
    {
        id: 'sg-101',
        title: 'Quick Intro',
        className: 'site-guide',
        time: '5 minutes',
        content: 'Learn why this site exists and explore one Clayville landmark, Bosch Chemical Solutions (don\'t miss the real-life stories and related key issues).',
        link_lead_in: 'Click on these links to discover:',
        image: '/site-guides-images/site-guides-01.png',
        routes: [
            { route: routes.whyWeAreHere, params: {}, label: 'Why We\'re Here' },
            { route: routes.clayvilleView, params: { location: 'bosch-chemical-solutions' }, label: 'Virtual Clayville: Bosch Chemical Solutions' }
        ]
    },
    {
        id: 'sg-102',
        title: 'Meet the Murphys',
        className: 'site-guide',
        time: '12 minutes',
        content: 'Systemic racism harms individuals, families and communities. See how systemic racism affects the lives of a grandmother and her grandson in Clayville.',
        link_lead_in: 'These links will take you to their tales:',
        image: '/site-guides-images/site-guides-02.png',
        routes: [
            { route: routes.murphysView, params: { character: 'joans-tale' }, label: 'Joan Berry' },
            { route: routes.murphysView, params: { character: 'jalens-tale' }, label: 'Jalen Murphy' }
        ]
    },
    {
        id: 'sg-103',
        title: 'Facts & Figures',
        className: 'site-guide',
        time: '10 minutes',
        content: 'Explore examples of how common policies and practices, and their interconnecting effects, drive systemic racism across key areas of society: health, education, wealth, housing and criminal justice.',
        link_lead_in: 'Use these links to learn about them:',
        image: '/site-guides-images/site-guides-03.png',
        routes: [
            { route: routes.keyIssuesIndex, params: {} },
            { route: routes.keyIssuesIndex, params: {}, urlParams: { category: 'Health' }, label: 'Health' },
            { route: routes.keyIssuesIndex, params: {}, urlParams: { category: 'Education' }, label: 'Education' }
        ]
    }
]

const mapStateToProps = (state) => {
    const { siteGuide, visitedRoutes, nav } = state
    return {
        siteGuideState: siteGuide,
        visitedRoutesState: visitedRoutes,
        navState: nav
    }
}

const SiteGuidesIndex = ({ dispatch, location, navigate, siteGuideState, visitedRoutesState, navState }) => {
    const componentName = 'Site Guides'
    const [activeGuides, setActiveGuides] = useState([])

    const componentState = useMemo(() => {
        return calcComponentState(componentName, navState)
    }, [navState, componentName])

    useEffect(() => {
        if (componentState === 'PRELOADING') {
            handlePreloadingState()
            if (navState.theme !== 'grey') {
                dispatch(saveNavTheme('grey'))
            }
        } else if (componentState === 'ANIMATING_IN') {
            handleAnimatingInState()
        } else if (componentState === 'ANIMATING_OUT') {
            handleAnimatingOutState()
        }
    }, [componentState])

    const handlePreloadingState = () => {
        dispatch(completePreloading())
    }

    const handleAnimatingInState = async () => {
        dispatch(clearMusicSource())
        const markerLine = animateLine()
        markerLine.finished.then(() => {
            dispatch(completeAnimatingIn())
        })
    }

    const handleAnimatingOutState = () => {
        setActiveGuides([])
        dispatch(completeAnimatingOut())
    }

    const animateLine = () => {
        return anime({
            targets: '.title-stroke #marker',
            strokeDashoffset: 0,
            easing: 'easeInQuart',
            duration: 750
        })
    }

    if (componentState === 'OFF' || componentState === 'PRELOADING') {
        return null
    }

    const handleNavigateSiteGuideClick = (siteGuideId, item) => {
        const siteguide = data.find(obj => {
            return obj.id === siteGuideId
        })
        if (siteguide) {
            if (siteguide.id === siteGuideState.id) {
                // I am already in this site guide, so just update the visited state with the link i just clicked on
                let stateRoutes = siteGuideState.routes.map((x) => ({ ...x }))
                stateRoutes.forEach((i) => {
                    let itemLink = buildRoutePath(item.route.path, item.params, item?.urlParams)
                    let tempToLink = buildRoutePath(i.route.path, i.params, i?.urlParams)
                    if (tempToLink === itemLink.replace(/\/$/, '')) {
                        i.visited = true
                    }
                })

                dispatch(saveSiteGuideVisitedRoute({ id: siteGuideState.id, title: siteGuideState.title, routes: stateRoutes }))
            } else {
                // I am not in this site guide, so reset the state and save the visited route
                let routesToSave = []
                let itemLink = buildRoutePath(item.route.path, item.params, item?.urlParams)
                siteguide.routes.forEach((i) => {
                    let tempToLink = buildRoutePath(i.route.path, i.params, i?.urlParams)
                    if (tempToLink === itemLink.replace(/\/$/, '')) {
                        i.visited = true
                    }
                    routesToSave.push({ ...i })
                })
                dispatch(saveSiteGuideVisitedRoute({ id: siteguide.id, title: siteguide.title, routes: routesToSave }))
            }
        }
        const navigateTo = buildRoutePath(item.route.path, item.params, item?.urlParams)
        navigate(navigateTo)
    }

    const renderLinks = (siteguide) => {
        let allLinks = []
        // NOTE: Commenting this out in case we fgo back, for now just load the first item into get started
        siteguide.routes.forEach((item, index) => {
            const routePath = buildRoutePath(item.route.path, item.params)
            const hasVisited = visitedRoutesState.visitedRoutes.find(obj => obj.toLink === routePath)

            const handleKeyDown = (e) => {
                if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                    e.preventDefault()
                    handleNavigateSiteGuideClick(siteguide.id, item)
                }
            }

            allLinks.push(
                <p key={index}>
                    <span tabIndex="0" role="button" aria-label={`Read about ${item.label}`}
                        onKeyDown={handleKeyDown} className={`site-guide-link nav-link ${hasVisited ? 'visited' : ''}`} onClick={() => handleNavigateSiteGuideClick(siteguide.id, item)}>
                        {item.label ? item.label : item.route.label}
                    </span>
                </p>
            )
        })
        return allLinks
    }

    const renderGetStarted = (siteguide) => {
        // let firstLink = siteguide.routes[0]
        // if (firstLink) {
        //     return (
        //         <button key={firstLink.id} className={`nav-link btn medium`} onClick={() => handleNavigateSiteGuideClick(siteguide.id, firstLink)}>
        //             Get Started
        //         </button>
        //     )
        // }
        return null
    }

    const renderSiteGuides = () => {
        let allGuides = []
        data.forEach(siteguide => {
            const isActive = activeGuides.indexOf(siteguide.id) !== -1
            allGuides.push(
                <div key={siteguide.id} className={`${siteguide.className} ${isActive ? 'active' : ''}`} >
                    <div className="image">
                        <img src={siteguide.image} alt={siteguide.title} />
                    </div>
                    <div className="content-wrapper">
                        <div className="header-container">
                            <div className="time-label-wrapper">
                                <div className="time">
                                    {siteguide.time}
                                </div>
                                <div className="label-header">
                                    {siteguide.title}
                                </div>
                            </div>
                        </div>
                        <div className="content">
                            <p>{siteguide.content}</p>
                            <p>{siteguide.link_lead_in}</p>
                            <div className="site-guide-links">
                                {renderLinks(siteguide)}
                            </div>
                        </div>
                    </div>
                    <div className="nav-links">
                        {renderGetStarted(siteguide)}
                    </div>
                </div>
            )
        })
        return allGuides
    }

    return (
        <div className="site-guides-index">
            <div className="container">
                <div className="title-stroke">
                    <h1>Site Guides</h1>
                    <MarkerStroke />
                </div>
                <div className="page-blurb">
                    <p>
                    These curated guides are designed as supplements to your self-directed, non-linear exploration of the site.
                    </p>
                    <p>
                        <Link to={routes.community.path}>Stay in touch to learn about new site guides and insights.</Link>
                    </p>
                </div>
                <div className="site-guide-wrapper">
                    {renderSiteGuides()}
                </div>
            </div>
        </div>
    )
}

export default withRouterProps(connect(mapStateToProps)(SiteGuidesIndex))
